<template>
  <q-banner
    v-bind="$attrs"
    :class="bannerClass"
    class="c-banner"
    :inline-actions="hasInlineActions()"
  >
    <template #avatar>
      <q-icon :name="icon" size="16px" />
    </template>

    <slot />

    <!-- Opt-in placement: actions below the text, aligned with it. -->
    <div v-if="hasStackedActions()" class="c-banner__actions">
      <slot name="action" />
    </div>

    <template v-if="hasInlineActions()" #action>
      <slot v-if="props.stackedActions === false" name="action" />

      <q-btn
        v-if="props.closable"
        flat
        dense
        round
        icon="fa-solid fa-xmark"
        size="sm"
        class="c-banner__close"
        @click="emit('close')"
      />
    </template>
  </q-banner>
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";
import { bannerIcons } from "common/defaultIcons";
import type { BannerType } from "shared/types";

const props = withDefaults(
  defineProps<{
    type?: BannerType;
    closable?: boolean;
    /** Put the action below the text instead of on the right of it. */
    stackedActions?: boolean;
  }>(),
  {
    type: "info",
    closable: false,
    stackedActions: false,
  },
);

const emit = defineEmits<{
  (e: "close"): void;
}>();

const slots = useSlots();

// Plain functions, not computeds: the slots object is not reactive, so a
// consumer passing `<template v-if="..." #action>` only renders correctly
// when the check runs at render time.
const hasStackedActions = () =>
  slots.action !== undefined && props.stackedActions;

const hasInlineActions = () =>
  (slots.action !== undefined && props.stackedActions === false) ||
  props.closable;

const icon = computed(() => bannerIcons[props.type]);

const bannerClass = computed(() => `c-banner--${props.type}`);
</script>

<style lang="scss">
$banner-colors: (
  "info": --color-info,
  "warning": --color-warning,
  "negative": --color-negative,
  "positive": --color-positive,
);

div.c-banner {
  @apply tw-min-h-[40px] tw-items-start tw-rounded-lg;

  .q-banner__avatar {
    @apply tw-flex tw-min-h-[24px] tw-items-center;
  }

  .q-banner__content {
    @apply tw-leading-6;
  }

  .q-banner__actions {
    @apply tw-flex tw-min-h-[24px] tw-items-center tw-self-start;
  }

  .c-banner__close {
    @apply tw-h-6 tw-w-6;
  }

  .c-banner__actions {
    @apply tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-3;
  }
}

@each $name, $color in $banner-colors {
  .c-banner--#{$name} {
    background: color-mix(in srgb, rgb(var(#{$color})) 10%, white);
    border: solid 1px color-mix(in srgb, rgb(var(#{$color})) 20%, white);

    .q-banner__avatar .q-icon {
      color: rgb(var(#{$color}));
    }
  }
}
</style>
